import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildDocContext, copyPlainText, docHtmlToPlainText, markdownToPlainText } from "../email-copy";
import { MarkdownView } from "../doc/MarkdownView";
import { buildEmailCampaign, campaignPlainText, campaignSpan, type CampaignEmail } from "../emails/email-campaign";
import { CampaignHeader, EmailCard, PhaseBand } from "../emails/CampaignTimeline";
import { EmailDetail } from "../emails/EmailDetail";
import "../emails/emails.css";

// The e-mail deliverable as a CAMPAIGN DESK (Pavel 2026-08-05). The document
// arrived as one 27 000 px sheet whose first e-mail sat twelve screens down
// and whose bodies were torn out of the phases that explained them; the
// review room showed the same sheet with no copy control at all. Here the
// campaign is drawn as a send timeline first, and an e-mail opens in place
// with its own copy buttons.
//
// One scroll surface on purpose: the reader's read-through gate, its chapter
// nav and its PDF export all watch this one sheet, and a nested second view
// would quietly break all three.

const COPY_FEEDBACK_MS = 2400;

function plainBody(body: string, html: boolean): string {
  return html ? docHtmlToPlainText(body) : markdownToPlainText(body);
}

export function EmailsCopyView(props: { md: string; t: (key: string) => string }) {
  const campaign = useMemo(() => buildEmailCampaign(props.md), [props.md]);
  const span = useMemo(() => campaignSpan(campaign), [campaign]);
  // The `<seg>` legend and term tips are facts of the WHOLE document; a
  // fragment rendered on its own would print raw segment keys
  // (`doc/doc-context.ts`), so the context is built once and handed down.
  const docContext = useMemo(() => (campaign.html ? buildDocContext(props.md) : undefined), [props.md, campaign.html]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState<Record<string, "done" | "failed">>({});
  const timers = useRef<Record<string, number>>({});
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const pendingScroll = useRef<number | null>(null);

  useEffect(() => {
    const handles = timers.current;
    return () => { for (const handle of Object.values(handles)) window.clearTimeout(handle); };
  }, []);

  // Opening or stepping to an e-mail puts its head at the top of the sheet:
  // without it a card near the bottom expands off-screen and the client is
  // left staring at the middle of a body they never scrolled to.
  useEffect(() => {
    const target = pendingScroll.current;
    if (target === null) return;
    pendingScroll.current = null;
    cardRefs.current[target]?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [openIndex]);

  const copy = useCallback(async (key: string, text: string) => {
    const delivered = await copyPlainText(text);
    window.clearTimeout(timers.current[key]);
    setCopied((state) => ({ ...state, [key]: delivered ? "done" : "failed" }));
    timers.current[key] = window.setTimeout(() => {
      setCopied((state) => {
        const next = { ...state };
        delete next[key];
        return next;
      });
    }, COPY_FEEDBACK_MS);
  }, []);

  const copyState = useCallback(
    (key: string): "idle" | "done" | "failed" => copied[key] ?? "idle",
    [copied]
  );

  const openEmail = useCallback((index: number) => {
    pendingScroll.current = index;
    setOpenIndex(index);
  }, []);

  // A document with no e-mail sections is not a campaign; it stays the plain
  // reader rather than pretending to be an empty timeline.
  if (campaign.emails.length === 0) return <MarkdownView md={props.md} />;

  const total = campaign.emails.length;
  // A clipboard the browser refused says so once, in words that tell the
  // client what to do instead; the button alone can only say it failed.
  const copyFailed = Object.values(copied).includes("failed");
  return (
    <div className="emails-campaign" data-email-count={total}>
      <CampaignHeader
        campaign={campaign}
        span={span}
        t={props.t}
        copyAllState={copyState("all")}
        // "Vše" on a campaign desk means every E-MAIL, in send order, ready to
        // paste - not the strategy chapters the client is reading, not the
        // briefing prose. Copying the raw document handed a mail tool the
        // reasoning as well.
        onCopyAll={() => void copy(
          "all",
          campaignPlainText(campaign, props.t("sales.emails.detail.subject"), (body) => plainBody(body, campaign.html))
        )}
      />

      {copyFailed && (
        <p className="message error-message ec-copy-error" role="status">{props.t("sales.emails.copyFailed")}</p>
      )}

      <div className="ec-timeline">
        {campaign.phases.map((phase, position) => (
          <PhaseBand
            key={`${phase.title ?? "phase"}-${position}`}
            phase={phase}
            fallbackTitle={props.t("sales.emails.phaseFallback")}
            countLabel={props.t("sales.emails.phaseCount").replace("{count}", String(phase.emails.length))}
          >
            {phase.emails.map((email) => (
              <div
                className="ec-slot"
                key={email.index}
                ref={(element) => { cardRefs.current[email.index] = element; }}
              >
                <EmailCard
                  email={email}
                  total={total}
                  open={openIndex === email.index}
                  copyState={copyState(`body:${email.index}`)}
                  subjectCopyState={copyState(`subject:${email.index}`)}
                  t={props.t}
                  onOpen={() => (openIndex === email.index
                    ? (() => { pendingScroll.current = email.index; setOpenIndex(null); })()
                    : openEmail(email.index))}
                  onCopy={() => void copy(`body:${email.index}`, plainBody(email.body, campaign.html))}
                  onCopySubject={() => void copy(`subject:${email.index}`, email.subject)}
                />
                {openIndex === email.index && (
                  <EmailDetail
                    email={email}
                    total={total}
                    html={campaign.html}
                    copyState={copyState}
                    t={props.t}
                    onCopyAltSubject={(subject, key) => void copy(key, subject)}
                    onStep={(delta) => openEmail(Math.min(total, Math.max(1, email.index + delta)))}
                  />
                )}
              </div>
            ))}
          </PhaseBand>
        ))}
      </div>

      {campaign.intro && (
        <section className="ec-strategy">
          <h3 className="doc-section ec-strategy-title">{props.t("sales.emails.strategyTitle")}</h3>
          <p className="ec-strategy-lead">{props.t("sales.emails.strategyLead")}</p>
          <MarkdownView md={campaign.intro} docHtml={campaign.html} docContext={docContext} />
        </section>
      )}
    </div>
  );
}
